/**
 * Página de Configuración
 *
 * Permite al usuario:
 * - Configurar PIN de bloqueo de pantalla
 * - Cambiar PIN existente
 * - Bloquear pantalla manualmente
 * - Ver estado del sistema de bloqueo
 * - Configurar porcentajes de servicio e IVA
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLock } from '../context/LockContext';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import {
  getRestaurant,
  updateRestaurant,
  updateRestaurantSettings,
  updateOrganization,
} from '../services/api';
import { useUserRole } from '../hooks/useUserRole';
import ChangePinModal from '../components/ChangePinModal';
import PaymentMethodsForm from '../components/PaymentMethodsForm';
import OrgPaymentDefaultsForm from '../components/OrgPaymentDefaultsForm';
import OrderModesForm from '../components/OrderModesForm';
import InventorySettingsSection from '../components/InventorySettingsSection';
import ComandaSecuritySection from '../components/ComandaSecuritySection';
import UpsellModal from '../components/UpsellModal';
import Icon from '../components/Icon';
import HelpIcon from '../components/help/HelpIcon';
import { storage } from '../utils/storage';
import './SettingsPage.css';

// El backend persiste orderModes en forma de objeto ({ dineIn:{enabled}, ... }).
// El form / endpoint PATCH /settings trabaja con un array de modos habilitados.
const ORDER_MODE_KEYS = [
  ['dine_in', 'dineIn'],
  ['takeaway', 'takeaway'],
  ['delivery', 'delivery'],
];

function orderModesToArray(modesObj) {
  if (Array.isArray(modesObj)) return modesObj;
  if (!modesObj || typeof modesObj !== 'object') return [];
  return ORDER_MODE_KEYS.filter(([, objKey]) => modesObj[objKey]?.enabled).map(([code]) => code);
}

// Limpia valores vacíos ('') antes de enviar al backend.
function cleanDeliverySettings(ds) {
  const out = {};
  Object.entries(ds || {}).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) out[k] = v;
  });
  return out;
}

function SettingsPage() {
  const { t } = useTranslation();
  const { hasFeature, getRequiredPlan, initialized: subscriptionInitialized } = useSubscription();
  const { organization, checkUser } = useAuth();
  const { isOwner } = useUserRole();
  // Defaults org-level de métodos de pago (NER-1284): proyectados en
  // GET /organizations/me. `null` = la org no tiene defaults configurados.
  const orgPaymentDefaults = organization?.paymentMethodDefaults || null;
  // Mientras no se inicializó la suscripción, no bloqueamos (el backend valida en el guardado).
  const deliveryAvailable = !subscriptionInitialized || hasFeature('delivery');
  // Identificación por mozo (RES-114): mismo criterio, el servidor valida al guardar.
  const staffIdentificationAvailable =
    !subscriptionInitialized || hasFeature('staffIdentification');
  const {
    hasPin,
    setPin: configurePinInContext,
    lock,
    autoLockEnabled,
    autoLockTime,
    setAutoLockEnabled,
    setAutoLockTime,
    removePin,
  } = useLock();
  const { preference, setPreference, autoMode, setAutoMode } = useTheme();

  const [showSetupPin, setShowSetupPin] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Billing settings state
  const [serviceCharge, setServiceCharge] = useState(10);
  const [taxRate, setTaxRate] = useState(21);
  const [autoCloseMinutes, setAutoCloseMinutes] = useState(30);
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingSuccess, setBillingSuccess] = useState('');

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const initialPaymentMethods = useRef(null);
  const isPaymentDirty =
    JSON.stringify(paymentMethods) !== JSON.stringify(initialPaymentMethods.current);

  // Org-level payment defaults state (NER-1285 item 5, OWNER). Se persisten vía
  // PUT /organizations/{id} con `paymentMethodDefaults` top-level (NER-1299):
  // el backend hace SET settings.paymentMethodDefaults (no clobbea el resto).
  const [orgDefaults, setOrgDefaults] = useState(orgPaymentDefaults || {});
  const [savingOrgDefaults, setSavingOrgDefaults] = useState(false);
  const [orgDefaultsError, setOrgDefaultsError] = useState('');
  const [orgDefaultsSuccess, setOrgDefaultsSuccess] = useState('');
  const initialOrgDefaults = useRef(orgPaymentDefaults || {});
  const isOrgDefaultsDirty =
    JSON.stringify(orgDefaults) !== JSON.stringify(initialOrgDefaults.current);
  // Políticas de inventario (RES-26): la sección maneja su propio estado y avisa si hay cambios
  const [isInventoryPoliciesDirty, setIsInventoryPoliciesDirty] = useState(false);

  // Seguridad de la comanda (RES-114): settings.comandaSecurity del local; la sección maneja su
  // propio formulario y avisa si hay cambios sin guardar
  const [comandaSecurity, setComandaSecurity] = useState(null);
  const [isComandaSecurityDirty, setIsComandaSecurityDirty] = useState(false);
  const [showStaffIdentificationUpsell, setShowStaffIdentificationUpsell] = useState(false);

  // Order modes state (single config aligned with PATCH /settings contract)
  const [orderConfig, setOrderConfig] = useState({
    orderModes: [],
    deliverySettings: {},
    restaurantLocation: {},
    ownDelivery: {},
  });
  const [savingOrderModes, setSavingOrderModes] = useState(false);
  const [orderModesError, setOrderModesError] = useState('');
  const [orderModesSuccess, setOrderModesSuccess] = useState('');
  const [showDeliveryUpsell, setShowDeliveryUpsell] = useState(false);
  const [deliveryRequiredPlan, setDeliveryRequiredPlan] = useState('professional');
  const initialOrderConfig = useRef(null);
  const isOrderModesDirty =
    JSON.stringify(orderConfig) !== JSON.stringify(initialOrderConfig.current);
  const selectedRestaurantId = storage.get('selectedRestaurantId');

  // Dirty state tracking for billing
  const initialBilling = useRef({ serviceCharge: 10, taxRate: 21, autoCloseMinutes: 30 });
  const isBillingDirty =
    String(serviceCharge) !== String(initialBilling.current.serviceCharge) ||
    String(taxRate) !== String(initialBilling.current.taxRate) ||
    String(autoCloseMinutes) !== String(initialBilling.current.autoCloseMinutes);
  const { showModal, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    isBillingDirty ||
      isPaymentDirty ||
      isOrderModesDirty ||
      isOrgDefaultsDirty ||
      isInventoryPoliciesDirty ||
      isComandaSecurityDirty
  );

  // Load restaurant settings on mount
  const loadRestaurantSettings = useCallback(async () => {
    const restaurantId = storage.get('selectedRestaurantId');
    if (!restaurantId) {
      setLoadingBilling(false);
      return;
    }

    try {
      setLoadingBilling(true);
      const restaurant = await getRestaurant(restaurantId);

      // Payment methods viven top-level en el restaurant (lo que lee el backend de pagos),
      // NO en settings. Fallback a legacy settings.paymentMethods por compat de datos viejos.
      const pm = restaurant?.paymentMethods || restaurant?.settings?.paymentMethods || null;
      setPaymentMethods(pm);
      initialPaymentMethods.current = pm ? JSON.parse(JSON.stringify(pm)) : null;

      setComandaSecurity(restaurant?.settings?.comandaSecurity || null);

      if (restaurant?.settings) {
        const sc = restaurant.settings.serviceCharge ?? 10;
        const tr = restaurant.settings.taxRate ?? 21;
        const acm = restaurant.settings.autoCloseMinutes ?? 30;
        setServiceCharge(sc);
        setTaxRate(tr);
        setAutoCloseMinutes(acm);
        initialBilling.current = { serviceCharge: sc, taxRate: tr, autoCloseMinutes: acm };

        const config = {
          orderModes: orderModesToArray(restaurant.settings.orderModes),
          deliverySettings: restaurant.settings.deliverySettings || {},
          restaurantLocation: restaurant.settings.restaurantLocation || {},
          ownDelivery: restaurant.settings.ownDelivery || {},
        };
        setOrderConfig(config);
        initialOrderConfig.current = JSON.parse(JSON.stringify(config));
      }
    } catch (err) {
      console.error('Error loading restaurant settings:', err);
    } finally {
      setLoadingBilling(false);
    }
  }, []);

  useEffect(() => {
    loadRestaurantSettings();
  }, [loadRestaurantSettings]);

  // Sincroniza el editor de defaults org-level cuando la organización carga/cambia
  // (AuthContext la resuelve async). Sólo mientras no haya ediciones sin guardar.
  useEffect(() => {
    if (isOrgDefaultsDirty) return;
    const next = orgPaymentDefaults || {};
    setOrgDefaults(next);
    initialOrgDefaults.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgPaymentDefaults]);

  // Close setup PIN modal on Escape key
  useEffect(() => {
    if (!showSetupPin) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowSetupPin(false);
        setPinInput('');
        setConfirmPinInput('');
        setError('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSetupPin]);

  // Save billing settings
  const handleSaveBilling = async () => {
    const restaurantId = storage.get('selectedRestaurantId');
    if (!restaurantId) {
      setBillingError(t('settings.billing.noRestaurant'));
      return;
    }

    // Validation
    if (serviceCharge < 0 || serviceCharge > 100) {
      setBillingError(t('settings.billing.invalidServiceCharge'));
      return;
    }
    if (taxRate < 0 || taxRate > 100) {
      setBillingError(t('settings.billing.invalidTaxRate'));
      return;
    }

    try {
      setSavingBilling(true);
      setBillingError('');

      // Fetch current settings to preserve other values
      const currentRestaurant = await getRestaurant(restaurantId);
      const currentSettings = currentRestaurant?.settings || {};

      await updateRestaurant(restaurantId, {
        settings: {
          ...currentSettings,
          serviceCharge: parseFloat(serviceCharge),
          taxRate: parseFloat(taxRate),
          autoCloseMinutes: parseInt(autoCloseMinutes, 10),
        },
      });

      initialBilling.current = {
        serviceCharge: parseFloat(serviceCharge),
        taxRate: parseFloat(taxRate),
        autoCloseMinutes: parseInt(autoCloseMinutes, 10),
      };
      setBillingSuccess(t('settings.billing.saveSuccess'));
      setTimeout(() => setBillingSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving billing settings:', err);
      setBillingError(t('settings.billing.saveError'));
    } finally {
      setSavingBilling(false);
    }
  };

  // Save payment methods
  const handleSavePaymentMethods = async () => {
    const restaurantId = storage.get('selectedRestaurantId');
    if (!restaurantId) {
      setPaymentError(t('settings.paymentMethods.noRestaurant'));
      return;
    }

    try {
      setSavingPayment(true);
      setPaymentError('');

      // paymentMethods se persiste top-level (source-of-truth que lee el backend de pagos),
      // NO dentro de settings. Escribir en settings.paymentMethods era un no-op silencioso.
      await updateRestaurant(restaurantId, { paymentMethods });

      initialPaymentMethods.current = JSON.parse(JSON.stringify(paymentMethods));
      setPaymentSuccess(t('settings.paymentMethods.saveSuccess'));
      setTimeout(() => setPaymentSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving payment methods:', err);
      setPaymentError(t('settings.paymentMethods.saveError'));
    } finally {
      setSavingPayment(false);
    }
  };

  // Save org-level payment defaults (NER-1285 item 5, OWNER). Envía el shape
  // acotado a campos heredables; el backend valida (400 si llega una credencial)
  // y restringe a owner (403 para admin). Refresca la org para que los
  // placeholders de herencia per-restaurant reflejen los nuevos defaults.
  const handleSaveOrgDefaults = async () => {
    const organizationId = storage.get('selectedOrganizationId');
    if (!organizationId) {
      setOrgDefaultsError(t('settings.orgPaymentDefaults.noOrganization'));
      return;
    }

    try {
      setSavingOrgDefaults(true);
      setOrgDefaultsError('');

      await updateOrganization(organizationId, { paymentMethodDefaults: orgDefaults });

      initialOrgDefaults.current = JSON.parse(JSON.stringify(orgDefaults));
      setOrgDefaultsSuccess(t('settings.orgPaymentDefaults.saveSuccess'));
      setTimeout(() => setOrgDefaultsSuccess(''), 3000);

      // Re-fetch de la org para refrescar organization.paymentMethodDefaults.
      try {
        await checkUser();
      } catch (refreshErr) {
        console.error('Error refreshing organization:', refreshErr);
      }
    } catch (err) {
      console.error('Error saving org payment defaults:', err);
      if (err.response?.status === 403) {
        setOrgDefaultsError(t('settings.orgPaymentDefaults.ownerOnly'));
      } else if (err.response?.status === 400 && err.response?.data?.message) {
        setOrgDefaultsError(err.response.data.message);
      } else {
        setOrgDefaultsError(t('settings.orgPaymentDefaults.saveError'));
      }
    } finally {
      setSavingOrgDefaults(false);
    }
  };

  // Save order modes via dedicated PATCH /settings endpoint (feature-gated)
  const handleSaveOrderModes = async () => {
    const restaurantId = storage.get('selectedRestaurantId');
    if (!restaurantId) {
      setOrderModesError(t('settings.orderModes.noRestaurant'));
      return;
    }

    const deliveryOn = orderConfig.orderModes.includes('delivery');
    const loc = orderConfig.restaurantLocation || {};
    const hasLocation =
      loc.lat !== undefined && loc.lat !== '' && loc.lng !== undefined && loc.lng !== '';

    // Validación client-side: delivery requiere ubicación.
    // La ubicación se configura en Edit Restaurant (NER-1262); acá solo se lee.
    if (deliveryOn && !hasLocation) {
      setOrderModesError(t('orderModes.delivery.locationRequired'));
      return;
    }

    try {
      setSavingOrderModes(true);
      setOrderModesError('');

      // restaurantLocation NO se envía: Edit Restaurant es source-of-truth (NER-1262).
      const payload = {
        orderModes: orderConfig.orderModes,
        deliverySettings: cleanDeliverySettings(orderConfig.deliverySettings),
        ownDelivery: orderConfig.ownDelivery || {},
      };

      await updateRestaurantSettings(restaurantId, payload);

      initialOrderConfig.current = JSON.parse(JSON.stringify(orderConfig));
      setOrderModesSuccess(t('settings.orderModes.saveSuccess'));
      setTimeout(() => setOrderModesSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving order modes:', err);
      if (err.response?.status === 403) {
        // Feature gate: plan no incluye delivery
        setDeliveryRequiredPlan(
          err.response.data?.requiredPlan ||
            err.response.headers?.['x-required-plan'] ||
            getRequiredPlan('delivery')
        );
        setShowDeliveryUpsell(true);
      } else if (err.response?.status === 400 && err.response?.data?.error) {
        setOrderModesError(err.response.data.error);
      } else {
        setOrderModesError(t('settings.orderModes.saveError'));
      }
    } finally {
      setSavingOrderModes(false);
    }
  };

  const handleOrderModesChange = useCallback((newConfig) => {
    setOrderConfig(newConfig);
  }, []);

  const handleDeliveryUpsell = useCallback(() => {
    setDeliveryRequiredPlan(getRequiredPlan('delivery'));
    setShowDeliveryUpsell(true);
  }, [getRequiredPlan]);

  // Configurar PIN por primera vez
  const handleSetupPin = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (pinInput.length < 4 || pinInput.length > 6 || !/^\d{4,6}$/.test(pinInput)) {
      setError(t('lock.setupError'));
      return;
    }

    if (pinInput !== confirmPinInput) {
      setError(t('lock.pinMismatch'));
      return;
    }

    try {
      configurePinInContext(pinInput);
      setSuccess(t('lock.setupSuccess'));
      setShowSetupPin(false);
      setPinInput('');
      setConfirmPinInput('');

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Bloquear pantalla manualmente
  const handleLockScreen = () => {
    lock();
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
        <p title={t('settings.subtitle')}>{t('settings.subtitle')}</p>
      </div>

      {/* Mensajes de éxito/error */}
      {success && (
        <div className="alert alert-success">
          <Icon name="check_circle" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <Icon name="error" />
          <span>{error}</span>
        </div>
      )}

      {/* Sección: Bloqueo de Pantalla */}
      <div className="card settings-section">
        <div className="section-header">
          <Icon name="lock" />
          <h2>{t('settings.panelLock')}</h2>
        </div>

        <div className="section-content">
          {/* Bloqueo de Pantalla (Toggle Principal) */}
          <div className="setting-item">
            <div className="setting-info">
              <h3>{t('lock.securityPin')}</h3>
              <p className="setting-description">
                {hasPin ? t('settings.pinConfigured') : t('settings.pinNotConfigured')}
              </p>
            </div>
            <div className="setting-action">
              <div className="status-actions">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={hasPin}
                    aria-label={t('settings.panelLock')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setShowSetupPin(true);
                      } else {
                        if (window.confirm(t('lock.removePinConfirmation'))) {
                          removePin();
                        }
                      }
                    }}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Cambiar PIN (Solo si está activo) */}
          {hasPin && (
            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('lock.changePin')}</h3>
                <p className="setting-description">{t('settings.changePinDescription')}</p>
              </div>
              <div className="setting-action">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowChangePin(true)}
                >
                  <Icon name="edit" />
                  {t('lock.changePin')}
                </button>
              </div>
            </div>
          )}

          {/* Bloqueo Automático */}
          {hasPin && (
            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.autoLock')}</h3>
                <p className="setting-description">
                  {t('settings.autoLockDescription', { minutes: autoLockTime })}
                </p>
              </div>
              <div className="setting-action">
                <div className="auto-lock-controls">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={autoLockEnabled}
                      aria-label={t('settings.autoLock')}
                      onChange={(e) => setAutoLockEnabled(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>

                  {autoLockEnabled && (
                    <select
                      className="time-select"
                      value={autoLockTime}
                      onChange={(e) => setAutoLockTime(Number(e.target.value))}
                    >
                      <option value={1}>1 min</option>
                      <option value={5}>5 min</option>
                      <option value={10}>10 min</option>
                      <option value={30}>30 min</option>
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bloquear Ahora */}
          {hasPin && (
            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.lockNow')}</h3>
                <p className="setting-description">{t('settings.lockNowDescription')}</p>
              </div>
              <div className="setting-action">
                <button type="button" className="btn btn-secondary" onClick={handleLockScreen}>
                  <Icon name="lock" />
                  {t('lock.lock')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sección: Seguridad de la comanda (RES-114): por local y en el servidor */}
      <ComandaSecuritySection
        restaurantId={selectedRestaurantId}
        loading={loadingBilling}
        savedConfig={comandaSecurity}
        featureAvailable={staffIdentificationAvailable}
        onUpsell={() => setShowStaffIdentificationUpsell(true)}
        onDirtyChange={setIsComandaSecurityDirty}
        onSaved={setComandaSecurity}
      />

      {/* Sección: Apariencia */}
      <div className="card settings-section">
        <div className="section-header">
          <Icon name="palette" />
          <h2>{t('theme.appearance')}</h2>
        </div>

        <div className="section-content">
          <div className="setting-item">
            <div className="setting-info">
              <h3>{t('theme.title')}</h3>
              <p className="setting-description">{t('theme.appearanceDescription')}</p>
            </div>
            <div className="setting-action">
              <div className="theme-selector" role="radiogroup" aria-label={t('theme.title')}>
                <button
                  type="button"
                  className={`theme-option ${preference === 'light' ? 'active' : ''}`}
                  role="radio"
                  aria-checked={preference === 'light'}
                  onClick={() => setPreference('light')}
                >
                  <Icon name="light_mode" />
                  <span>{t('theme.light')}</span>
                </button>
                <button
                  type="button"
                  className={`theme-option ${preference === 'dark' ? 'active' : ''}`}
                  role="radio"
                  aria-checked={preference === 'dark'}
                  onClick={() => setPreference('dark')}
                >
                  <Icon name="dark_mode" />
                  <span>{t('theme.dark')}</span>
                </button>
                <button
                  type="button"
                  className={`theme-option ${preference === 'auto' ? 'active' : ''}`}
                  role="radio"
                  aria-checked={preference === 'auto'}
                  onClick={() => setPreference('auto')}
                >
                  <Icon name="brightness_auto" />
                  <span>{t('theme.auto')}</span>
                </button>
              </div>
              {preference === 'auto' && (
                <div
                  className="theme-selector theme-submode"
                  role="radiogroup"
                  aria-label={t('theme.autoModeLabel')}
                >
                  <button
                    type="button"
                    className={`theme-option ${autoMode === 'system' ? 'active' : ''}`}
                    role="radio"
                    aria-checked={autoMode === 'system'}
                    onClick={() => setAutoMode('system')}
                  >
                    <Icon name="computer" />
                    <span>{t('theme.autoModeSystem')}</span>
                  </button>
                  <button
                    type="button"
                    className={`theme-option ${autoMode === 'time-of-day' ? 'active' : ''}`}
                    role="radio"
                    aria-checked={autoMode === 'time-of-day'}
                    onClick={() => setAutoMode('time-of-day')}
                  >
                    <Icon name="schedule" />
                    <span>{t('theme.autoModeTimeOfDay')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sección: Facturación */}
      <div className="card settings-section">
        <div className="section-header">
          <Icon name="receipt_long" />
          <h2>{t('settings.billing.title')}</h2>
          {isBillingDirty && <span className="dirty-badge">{t('common.unsavedChangesTitle')}</span>}
        </div>

        <div className="section-content">
          {/* Mensajes de éxito/error de facturación */}
          {billingSuccess && (
            <div className="alert alert-success">
              <Icon name="check_circle" />
              <span>{billingSuccess}</span>
            </div>
          )}

          {billingError && (
            <div className="alert alert-error">
              <Icon name="error" />
              <span>{billingError}</span>
            </div>
          )}

          {loadingBilling ? (
            <div className="loading-state">
              <Icon name="hourglass_empty" />
              <span>{t('common.loading')}</span>
            </div>
          ) : (
            <>
              {/* Cargo por Servicio */}
              <div className="setting-item">
                <div className="setting-info">
                  <h3>{t('settings.billing.serviceCharge')}</h3>
                  <p className="setting-description">
                    {t('settings.billing.serviceChargeDescription')}
                  </p>
                </div>
                <div className="setting-action">
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={serviceCharge}
                      onChange={(e) => setServiceCharge(e.target.value)}
                      className="percentage-input"
                    />
                    <span className="input-suffix">%</span>
                  </div>
                </div>
              </div>

              {/* IVA / Tax Rate */}
              <div className="setting-item">
                <div className="setting-info">
                  <h3>{t('settings.billing.taxRate')}</h3>
                  <p className="setting-description">{t('settings.billing.taxRateDescription')}</p>
                </div>
                <div className="setting-action">
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      className="percentage-input"
                    />
                    <span className="input-suffix">%</span>
                  </div>
                </div>
              </div>

              {/* Auto-close tables */}
              <div className="setting-item">
                <div className="setting-info">
                  <h3>
                    {t('settings.billing.autoCloseMinutes')}{' '}
                    <HelpIcon topicKey="autoClose.minutes" />
                  </h3>
                  <p className="setting-description">
                    {t('settings.billing.autoCloseDescription')}
                  </p>
                </div>
                <div className="setting-action">
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      min="0"
                      max="480"
                      step="5"
                      value={autoCloseMinutes}
                      onChange={(e) => setAutoCloseMinutes(e.target.value)}
                      className="percentage-input"
                    />
                    <span className="input-suffix">min</span>
                  </div>
                </div>
              </div>

              {/* Botón Guardar */}
              <div className="setting-item billing-actions">
                <div className="setting-info">
                  <p className="setting-description billing-note">{t('settings.billing.note')}</p>
                </div>
                <div className="setting-action">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveBilling}
                    disabled={savingBilling || !isBillingDirty}
                  >
                    {savingBilling ? (
                      <>
                        <Icon name="hourglass_empty" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Icon name="save" />
                        {t('common.save')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sección: Métodos de Pago */}
      <div className="card settings-section">
        <div className="section-header">
          <Icon name="payments" />
          <h2>{t('settings.paymentMethods.title')}</h2>
          <span className="scope-badge scope-badge--restaurant">
            <Icon name="store" size="small" />
            {t('settings.scopeBadge.restaurant')}
          </span>
          {isPaymentDirty && <span className="dirty-badge">{t('common.unsavedChangesTitle')}</span>}
        </div>

        <div className="section-content">
          {paymentSuccess && (
            <div className="alert alert-success">
              <Icon name="check_circle" />
              <span>{paymentSuccess}</span>
            </div>
          )}

          {paymentError && (
            <div className="alert alert-error">
              <Icon name="error" />
              <span>{paymentError}</span>
            </div>
          )}

          {loadingBilling ? (
            <div className="loading-state">
              <Icon name="hourglass_empty" />
              <span>{t('common.loading')}</span>
            </div>
          ) : (
            <>
              <p className="setting-description" style={{ marginBottom: 'var(--space-md)' }}>
                {t('settings.paymentMethods.subtitle')}
              </p>
              <PaymentMethodsForm
                paymentMethods={paymentMethods}
                orgDefaults={orgPaymentDefaults}
                onChange={setPaymentMethods}
              />
              <div className="setting-item billing-actions">
                <div className="setting-info" />
                <div className="setting-action">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSavePaymentMethods}
                    disabled={savingPayment || !isPaymentDirty}
                  >
                    {savingPayment ? (
                      <>
                        <Icon name="hourglass_empty" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Icon name="save" />
                        {t('common.save')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sección: Defaults de métodos de pago de la organización (OWNER) */}
      {isOwner() && (
        <div className="card settings-section settings-section--org-scope">
          <div className="section-header">
            <Icon name="account_tree" />
            <h2>
              {t('settings.orgPaymentDefaults.title')}{' '}
              <HelpIcon topicKey="paymentMethods.orgDefaults" />
            </h2>
            <span className="scope-badge scope-badge--org">
              <Icon name="account_tree" size="small" />
              {t('settings.scopeBadge.organization')}
            </span>
            {isOrgDefaultsDirty && (
              <span className="dirty-badge">{t('common.unsavedChangesTitle')}</span>
            )}
          </div>

          <div className="section-content">
            {orgDefaultsSuccess && (
              <div className="alert alert-success">
                <Icon name="check_circle" />
                <span>{orgDefaultsSuccess}</span>
              </div>
            )}

            {orgDefaultsError && (
              <div className="alert alert-error">
                <Icon name="error" />
                <span>{orgDefaultsError}</span>
              </div>
            )}

            <p className="setting-description" style={{ marginBottom: 'var(--space-md)' }}>
              {t('settings.orgPaymentDefaults.subtitle')}
            </p>
            <OrgPaymentDefaultsForm value={orgDefaults} onChange={setOrgDefaults} />
            <div className="setting-item billing-actions">
              <div className="setting-info" />
              <div className="setting-action">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveOrgDefaults}
                  disabled={savingOrgDefaults || !isOrgDefaultsDirty}
                >
                  {savingOrgDefaults ? (
                    <>
                      <Icon name="hourglass_empty" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Icon name="save" />
                      {t('common.save')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección: Políticas de inventario de la organización (RES-26) */}
      <InventorySettingsSection onDirtyChange={setIsInventoryPoliciesDirty} />

      {/* Sección: Modalidades de Pedido */}
      <div className="card settings-section">
        <div className="section-header">
          <Icon name="shopping_cart" />
          <h2>{t('settings.orderModes.title')}</h2>
          {isOrderModesDirty && (
            <span className="dirty-badge">{t('common.unsavedChangesTitle')}</span>
          )}
        </div>

        <div className="section-content">
          {orderModesSuccess && (
            <div className="alert alert-success">
              <Icon name="check_circle" />
              <span>{orderModesSuccess}</span>
            </div>
          )}

          {orderModesError && (
            <div className="alert alert-error">
              <Icon name="error" />
              <span>{orderModesError}</span>
            </div>
          )}

          {loadingBilling ? (
            <div className="loading-state">
              <Icon name="hourglass_empty" />
              <span>{t('common.loading')}</span>
            </div>
          ) : (
            <>
              <p className="setting-description" style={{ marginBottom: 'var(--space-md)' }}>
                {t('settings.orderModes.subtitle')}
              </p>
              <OrderModesForm
                value={orderConfig}
                onChange={handleOrderModesChange}
                restaurantId={selectedRestaurantId}
                deliveryAvailable={deliveryAvailable}
                onDeliveryUpsell={handleDeliveryUpsell}
              />
              <div className="setting-item billing-actions">
                <div className="setting-info" />
                <div className="setting-action">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveOrderModes}
                    disabled={savingOrderModes || !isOrderModesDirty}
                  >
                    {savingOrderModes ? (
                      <>
                        <Icon name="hourglass_empty" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Icon name="save" />
                        {t('common.save')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal: Configurar PIN por Primera Vez */}
      {showSetupPin && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowSetupPin(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('lock.setupPin')}</h2>
              <button type="button" className="close-btn" onClick={() => setShowSetupPin(false)}>
                <Icon name="close" />
              </button>
            </div>

            <form onSubmit={handleSetupPin}>
              <div className="form-group">
                <label htmlFor="pin">{t('lock.setupPin')}</label>
                <input
                  type="password"
                  id="pin"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  placeholder="••••••"
                  maxLength="6"
                  minLength="4"
                  autoFocus
                  className="pin-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPin">{t('lock.confirmPin')}</label>
                <input
                  type="password"
                  id="confirmPin"
                  value={confirmPinInput}
                  onChange={(e) => setConfirmPinInput(e.target.value)}
                  placeholder="••••••"
                  maxLength="6"
                  minLength="4"
                  className="pin-input"
                />
              </div>

              <p className="form-help">{t('lock.setupHint')}</p>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setShowSetupPin(false);
                    setPinInput('');
                    setConfirmPinInput('');
                    setError('');
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Cambiar PIN */}
      {showChangePin && <ChangePinModal onClose={() => setShowChangePin(false)} />}

      {/* Modal: Upsell delivery (feature gate) */}
      {showDeliveryUpsell && (
        <UpsellModal
          feature="delivery"
          requiredPlan={deliveryRequiredPlan}
          onClose={() => setShowDeliveryUpsell(false)}
        />
      )}

      {/* Modal: Upsell identificación por mozo (feature gate) */}
      {showStaffIdentificationUpsell && (
        <UpsellModal
          feature="staffIdentification"
          requiredPlan={getRequiredPlan('staffIdentification')}
          onClose={() => setShowStaffIdentificationUpsell(false)}
        />
      )}

      {/* Modal: Cambios sin guardar */}
      {showModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-changes-title"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="unsaved-changes-title">{t('common.unsavedChangesTitle')}</h2>
            </div>
            <div className="modal-body">
              <p>{t('common.unsavedChangesMessage')}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={cancelNavigation}>
                {t('common.unsavedChangesStay')}
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmNavigation}>
                {t('common.unsavedChangesLeave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
